import { useApolloClient, useQuery } from "@apollo/client/react";
import type { ApolloClient } from "@apollo/client";
import type { PolicyReread } from "./policy";
import {
  GET_PROFESSIONAL_PROFILE,
  RESOLVE_BOOKING_SLUG,
  type GetProfessionalProfileData,
  type PublicProfessionalProfile,
  type ResolveBookingSlugData,
  type ResolveOutcome,
} from "./operations";

/**
 * THE PRE-SEND RE-READ (rc.10), outside the hook so the React Compiler keeps
 * compiling the hook (a try/catch in the hook's closure made it bail out:
 * measured, the hook's compiled output shrank by 739 B). The query the page
 * already watches, with its exact variables, so the answer updates the Review
 * on screen. Network only: never the cache. One silent retry, then "failed".
 */
async function readPolicyAgain(client: ApolloClient, variables: { professionalId: string | undefined; locale: "EN" | "ES"; withEnglish: boolean }): Promise<PolicyReread> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data } = await client.query<GetProfessionalProfileData>({ query: GET_PROFESSIONAL_PROFILE, variables, fetchPolicy: "network-only" });
      const read = data?.getProfessionalProfile;
      // The same resolution the page shows (English when Spanish has none).
      if (read) return { kind: "read", policy: read.cancellationPolicyDisplay ?? read.cancellationPolicyDisplayEnglish ?? null };
    } catch {
      // One silent retry; the second failure stops the send.
    }
  }
  return { kind: "failed" };
}

/**
 * The state a booking page can be in.
 *
 * `moved` is separate from `ready` because it is an instruction, not a
 * variation: the caller must redirect to `canonicalSlug`. Collapsing them would
 * leave the old name in the address bar and in anything the visitor copies or
 * shares onward, which is precisely what the alias mechanism exists to stop.
 */
export type BookingProfileState =
  | { status: "loading" }
  | {
      status: "ready";
      slug: string;
      profile: PublicProfessionalProfile;
      /**
       * rc.10: read the cancellation policy AGAIN, from the network, just
       * before a send (signed-in Submit, guest Continue and Verify). One silent
       * retry, then "failed" (the send stops, fail closed). The answer lands in
       * the cache, so the Review on screen shows what was read.
       */
      rereadPolicy: () => Promise<PolicyReread>;
    }
  | { status: "moved"; canonicalSlug: string }
  | { status: "not-bookable"; profile: PublicProfessionalProfile }
  | { status: "unavailable" }
  | { status: "error"; retry: () => void };

/**
 * Resolve a booking-link slug to a professional's public profile.
 *
 * Two hops, because the public API keeps them separate: slug →
 * `professionalId` → profile. The second only runs for outcomes that carry an
 * id, so `UNAVAILABLE` costs one round trip rather than two.
 */
export function useBookingProfile(slug: string | undefined, locale: "EN" | "ES" = "EN"): BookingProfileState {
  const resolved = useQuery<ResolveBookingSlugData>(RESOLVE_BOOKING_SLUG, {
    variables: { slug },
    skip: !slug,
  });

  const outcome: ResolveOutcome | undefined = resolved.data?.resolveBookingSlug.outcome;
  const professionalId = resolved.data?.resolveBookingSlug.professionalId ?? undefined;
  const canonicalSlug = resolved.data?.resolveBookingSlug.canonicalSlug ?? undefined;

  // MOVED carries a professionalId too, but fetching the profile for it would
  // render a page at the OLD url — the redirect has to happen first.
  const wantsProfile = outcome === "RESOLVED" || outcome === "NOT_BOOKABLE";

  const client = useApolloClient();
  const profile = useQuery<GetProfessionalProfileData>(GET_PROFESSIONAL_PROFILE, {
    variables: { professionalId, locale, withEnglish: locale === "ES" },
    skip: !wantsProfile || !professionalId,
  });
  // A language switch is a new read (the locale is part of the cache key).
  // Until it lands, keep showing THIS professional's previous answer rather
  // than a loading screen; never another professional's. If it FAILS, keep
  // that answer too (its policy in the last language loaded) rather than
  // LoadFailed mid-flow: the profile is read once and only a switch reads it
  // again, so a failure with this professional's answer in hand is a switch's.
  const previous = profile.previousData?.getProfessionalProfile;
  const keepPrevious = !profile.data && previous !== undefined && previous.professionalId === professionalId;
  const profileData = profile.data ?? (keepPrevious ? profile.previousData : undefined);

  // The plain refetch. Try again is the screen's LoadFailed, which wraps this
  // in retryApiRead: a reload instead when App Check is why the read failed.
  const retry = () => {
    void resolved.refetch();
    if (wantsProfile) void profile.refetch();
  };

  if (resolved.error) return { status: "error", retry };
  if (resolved.loading || !outcome) return { status: "loading" };

  if (outcome === "UNAVAILABLE") return { status: "unavailable" };

  if (outcome === "MOVED") {
    // A MOVED with no canonicalSlug would be a backend contract violation. Fall
    // back to `unavailable` rather than redirecting to `/undefined`, which
    // would render as a wrong-shaped URL and blame the visitor for our bug.
    if (!canonicalSlug) return { status: "unavailable" };
    return { status: "moved", canonicalSlug };
  }

  if (profile.error && !keepPrevious) return { status: "error", retry };
  if (!profileData) return { status: "loading" };
  const raw = profileData.getProfessionalProfile;
  // Spanish with no approved wording (null) shows the English answer; a null
  // English answer still shows nothing (the contract's fail-closed rule).
  const policy = raw.cancellationPolicyDisplay ?? raw.cancellationPolicyDisplayEnglish ?? null;
  const { cancellationPolicyDisplayEnglish: _english, ...rest } = raw;
  const resolvedProfile = { ...rest, cancellationPolicyDisplay: policy };

  if (outcome === "NOT_BOOKABLE") {
    return { status: "not-bookable", profile: resolvedProfile };
  }

  const rereadPolicy = () => readPolicyAgain(client, { professionalId, locale, withEnglish: locale === "ES" });

  return {
    status: "ready",
    // Prefer the canonical slug for anything persisted or shared onward.
    slug: canonicalSlug ?? slug ?? "",
    profile: resolvedProfile,
    rereadPolicy,
  };
}
